import { useCallback } from "react";
import { Control, Path, useWatch } from "react-hook-form";

import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { grey } from "@mui/material/colors";
import type { SxProps } from "@mui/material/styles";

import CheckboxFormField from "components/checkbox-form-field";
import RecurrenceRuleEditor from "components/recurrence-rule-editor";
import type { RRuleResult } from "components/recurrence-rule-editor/utils";

import { AppointmentFormValues } from "../appointment-form.component";

type RecurringPeriodFieldProps = {
  control: Control<AppointmentFormValues, object>;
  recurrenceReadonly?: boolean;
  setValue: (
    name: Path<AppointmentFormValues>,
    value: any,
    options?: any
  ) => void;
  open?: boolean;
  setOpen?: (value: boolean) => void;
  sx?: SxProps;
};

export const DEFAULT_RRULE_VALUE = "RRULE:FREQ=WEEKLY;INTERVAL=1";

export default function RecurrenceRuleEditorField({
  control,
  setValue,
  recurrenceReadonly = false,
  open,
  setOpen,
  sx
}: RecurringPeriodFieldProps) {
  const [isRecurring, schedule] = useWatch({
    control,
    name: ["is_recurring", "schedule"]
  });

  const onCancel = useCallback(() => {
    if (!schedule?.rrule) {
      setValue("is_recurring", false);
    }
    setOpen?.(false);
  }, [setOpen, schedule?.rrule, setValue]);

  const onSubmit = useCallback(
    (result: RRuleResult) => {
      setOpen?.(false);
      setValue("schedule.rrule", String(result.rrule), {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, setOpen]
  );

  return (
    <Box display="flex" justifyContent="flex-start" alignItems="flex-start">
      <EventRepeatIcon sx={{ mt: 1.5, mr: 1.5 }} />

      <Stack
        direction="row"
        justifyContent="flex-start"
        sx={{ minWidth: "131px", mr: 0.35 }}
      >
        <FormControlLabel
          label="Is recurring"
          labelPlacement="start"
          sx={{ ml: 0 }}
          disabled={recurrenceReadonly}
          control={
            <CheckboxFormField
              testId="is_recurring"
              name="is_recurring"
              control={control}
              onClick={(value) => {
                if (value && !schedule?.rrule) {
                  setValue("schedule.rrule", DEFAULT_RRULE_VALUE, {
                    shouldDirty: true,
                    shouldValidate: true
                  });
                  setOpen?.(true);
                }
              }}
            />
          }
        />
      </Stack>

      {isRecurring && (
        <Stack
          direction="column"
          spacing={2.5}
          width={open ? "100%" : undefined}
          sx={{
            background: grey[50],
            borderRadius: "6px",
            border: `solid 1px ${grey[100]}`,
            ...sx
          }}
        >
          <RecurrenceRuleEditor
            initialRule={schedule?.rrule || ""}
            open={open}
            setOpen={setOpen}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        </Stack>
      )}
    </Box>
  );
}
